import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/common/Layout';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import { planMantenimientoService } from '../services/planMantenimiento';
import { activosService } from '../services/activos';
import toast from 'react-hot-toast';
import {
    CalendarClock, Plus, Upload, Download, Trash2, Edit, Play, X, PauseCircle, PlayCircle,
} from 'lucide-react';

const FRECUENCIA_LABEL = { dias: 'día(s)', semanas: 'semana(s)', meses: 'mes(es)', anios: 'año(s)' };
const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Dic'];

// "2,5,8,11" <-> [2, 5, 8, 11]
const parsearMeses = (texto) => (texto || '').split(',').map((m) => parseInt(m, 10)).filter((m) => m >= 1 && m <= 12);

// Planes del Excel anual: uno por sede y mantenimiento, en los meses marcados
// (sin activo). Los demás son por activo y se repiten según la frecuencia.
const esPlanPorSede = (plan) => !plan?.activo_id && !!plan?.sede;

const SelectorMeses = ({ meses, onChange }) => (
    <div className="grid grid-cols-6 gap-1.5">
        {MESES.map((nombre, idx) => {
            const mes = idx + 1;
            const marcado = meses.includes(mes);
            return (
                <button
                    key={mes}
                    type="button"
                    onClick={() => onChange(marcado ? meses.filter((m) => m !== mes) : [...meses, mes].sort((a, b) => a - b))}
                    className={`py-1.5 rounded-lg text-xs font-semibold border ${
                        marcado ? 'bg-green-100 border-green-400 text-green-800' : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                >
                    {nombre}
                </button>
            );
        })}
    </div>
);

// ─── Modal: crear / editar plan ─────────────────────────────────────────────

const ModalPlan = ({ plan, activos, sedes, onCerrar, onGuardado }) => {
    const esEdicion = !!plan;
    const [porSede, setPorSede] = useState(esPlanPorSede(plan));
    const [activoId, setActivoId] = useState(plan?.activo_id || '');
    const [sede, setSede] = useState(plan?.sede || '');
    const [meses, setMeses] = useState(parsearMeses(plan?.meses));
    const [cantidad, setCantidad] = useState(plan?.cantidad ?? '');
    const [area, setArea] = useState(plan?.area || '');
    const [tipo, setTipo] = useState(plan?.tipo_mantenimiento || '');
    const [frecuenciaValor, setFrecuenciaValor] = useState(plan?.frecuencia_valor || 1);
    const [frecuenciaTipo, setFrecuenciaTipo] = useState(plan?.frecuencia_tipo || 'meses');
    const [fechaProxima, setFechaProxima] = useState(plan?.fecha_proxima || '');
    const [responsable, setResponsable] = useState(plan?.responsable || '');
    const [notas, setNotas] = useState(plan?.notas || '');
    const [guardando, setGuardando] = useState(false);

    const handleGuardar = async () => {
        if (!esEdicion && !porSede && !activoId) { toast.error('Selecciona un activo'); return; }
        if (porSede && !sede.trim()) { toast.error('Indica la sede'); return; }
        if (porSede && meses.length === 0) { toast.error('Marca al menos un mes'); return; }
        if (!tipo.trim()) { toast.error('Indica el tipo de mantenimiento'); return; }
        if (!porSede && !fechaProxima) { toast.error('Indica la próxima fecha'); return; }

        const payload = {
            tipo_mantenimiento: tipo.trim(),
            frecuencia_valor: porSede ? Math.max(1, Math.round(12 / meses.length)) : parseInt(frecuenciaValor, 10),
            frecuencia_tipo: porSede ? 'meses' : frecuenciaTipo,
            responsable: responsable || null,
            notas: notas || null,
        };
        if (porSede) {
            payload.sede = sede.trim();
            payload.meses = meses.join(',');
            payload.cantidad = cantidad === '' ? null : parseInt(cantidad, 10);
            payload.area = area.trim() || null;
        }
        // En los planes por sede la próxima fecha la calcula el sistema a partir de
        // los meses (día 1 del próximo mes marcado), salvo que la cambien a mano.
        if (!porSede || (fechaProxima && fechaProxima !== (plan?.fecha_proxima || ''))) {
            payload.fecha_proxima = fechaProxima;
        } else if (!esEdicion) {
            const hoy = new Date();
            for (let salto = 1; salto <= 12; salto += 1) {
                const d = new Date(hoy.getFullYear(), hoy.getMonth() + salto, 1);
                if (meses.includes(d.getMonth() + 1)) {
                    payload.fecha_proxima = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
                    break;
                }
            }
        }

        setGuardando(true);
        try {
            if (esEdicion) {
                await planMantenimientoService.actualizar(plan.id, payload);
                toast.success('Plan actualizado');
            } else {
                await planMantenimientoService.crear({ ...payload, activo_id: parseInt(activoId, 10) });
                toast.success('Plan creado');
            }
            onGuardado();
            onCerrar();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error al guardar el plan');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal title={esEdicion ? 'Editar plan de mantenimiento' : 'Nuevo plan de mantenimiento'} onClose={onCerrar} size="sm">
            <div className="space-y-4">
                {!esEdicion && (
                    <div className="grid grid-cols-2 gap-2">
                        {[[false, 'Por activo'], [true, 'Por sede (plan anual)']].map(([valor, etiqueta]) => (
                            <button
                                key={etiqueta}
                                type="button"
                                onClick={() => setPorSede(valor)}
                                className={`py-2 rounded-lg text-sm font-semibold border ${
                                    porSede === valor ? 'bg-indigo-50 border-indigo-400 text-indigo-700' : 'border-gray-300 text-gray-500'
                                }`}
                            >
                                {etiqueta}
                            </button>
                        ))}
                    </div>
                )}

                {porSede && (
                    <>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Sede</label>
                            <input
                                type="text"
                                list="sedes-plan"
                                value={sede}
                                onChange={(e) => setSede(e.target.value)}
                                placeholder="Ej: Ica, Qolqas..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                            <datalist id="sedes-plan">
                                {sedes.map((s) => <option key={s} value={s} />)}
                            </datalist>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Meses en que se realiza</label>
                            <SelectorMeses meses={meses} onChange={setMeses} />
                            <p className="text-xs text-gray-400 mt-1">La orden de trabajo se genera el día 1 de cada mes marcado, todos los años.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Cantidad</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={cantidad}
                                    onChange={(e) => setCantidad(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Área (opcional)</label>
                                <input
                                    type="text"
                                    value={area}
                                    onChange={(e) => setArea(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                            </div>
                        </div>
                    </>
                )}

                {!esEdicion && !porSede && (
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Activo</label>
                        <select
                            value={activoId}
                            onChange={(e) => setActivoId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">Seleccionar activo...</option>
                            {activos.map((a) => (
                                <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo de mantenimiento</label>
                    <input
                        type="text"
                        value={tipo}
                        onChange={(e) => setTipo(e.target.value)}
                        placeholder="Ej: Cambio de filtro, Lubricación..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                {!porSede && (
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Frecuencia</label>
                        <input
                            type="number"
                            min="1"
                            value={frecuenciaValor}
                            onChange={(e) => setFrecuenciaValor(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Cada</label>
                        <select
                            value={frecuenciaTipo}
                            onChange={(e) => setFrecuenciaTipo(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            {Object.entries(FRECUENCIA_LABEL).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                            ))}
                        </select>
                    </div>
                </div>
                )}

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Próxima fecha{porSede ? ' (opcional: se calcula con los meses)' : ''}
                    </label>
                    <input
                        type="date"
                        value={fechaProxima}
                        onChange={(e) => setFechaProxima(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                        {porSede ? 'Proveedor (opcional)' : 'Responsable (opcional)'}
                    </label>
                    <input
                        type="text"
                        value={responsable}
                        onChange={(e) => setResponsable(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Notas (opcional)</label>
                    <textarea
                        value={notas}
                        onChange={(e) => setNotas(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
                <button onClick={onCerrar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                    Cancelar
                </button>
                <Button onClick={handleGuardar} loading={guardando} disabled={guardando}>
                    Guardar
                </Button>
            </div>
        </Modal>
    );
};

// ─── Modal: cargar Excel ─────────────────────────────────────────────────────

const ModalCargarExcel = ({ onCerrar, onCargado }) => {
    const [archivo, setArchivo] = useState(null);
    const [subiendo, setSubiendo] = useState(false);
    const [resultado, setResultado] = useState(null);

    const handleSubir = async () => {
        if (!archivo) { toast.error('Selecciona un archivo Excel'); return; }
        setSubiendo(true);
        try {
            const res = await planMantenimientoService.cargarExcel(archivo);
            setResultado(res);
            toast.success(res.message || 'Archivo procesado');
            onCargado();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error al procesar el archivo');
        } finally {
            setSubiendo(false);
        }
    };

    return (
        <Modal title="Cargar plan de mantenimiento (Excel)" onClose={onCerrar} size="sm">
            <div className="space-y-4">
                <button
                    onClick={() => planMantenimientoService.descargarPlantilla()}
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                    <Download className="h-4 w-4 mr-1" /> Descargar plantilla de ejemplo
                </button>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Archivo Excel</label>
                    <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => setArchivo(e.target.files[0] || null)}
                        className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Puedes subir directamente el <strong>plan anual de mantenciones</strong> (bloques
                        "Plan de mantenimiento anual - Sede" con los meses pintados): se crea un plan por
                        sede y mantenimiento, y la orden de trabajo sale el día 1 de cada mes marcado.
                        Volver a subirlo actualiza los planes existentes, no los duplica.
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                        También acepta la plantilla por activo: activo_code, tipo_mantenimiento, frecuencia_valor,
                        frecuencia_tipo (dias/semanas/meses/anios), fecha_proxima, responsable, notas.
                    </p>
                </div>

                {resultado && (
                    <div className="text-sm bg-gray-50 rounded-lg p-3">
                        <p className="font-semibold text-gray-700">
                            Creados: {resultado.creados}
                            {resultado.actualizados !== undefined && ` · Actualizados: ${resultado.actualizados}`}
                        </p>
                        {resultado.sedes?.length > 0 && (
                            <p className="text-xs text-gray-600 mt-1">Sedes: {resultado.sedes.join(', ')}</p>
                        )}
                        {resultado.omitidos?.length > 0 && (
                            <>
                                <p className="text-xs font-semibold text-amber-700 mt-2">No se importaron (sin meses marcados):</p>
                                <ul className="text-xs text-amber-700 list-disc list-inside space-y-0.5 max-h-32 overflow-y-auto">
                                    {resultado.omitidos.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            </>
                        )}
                        {resultado.errores?.length > 0 && (
                            <ul className="mt-2 text-xs text-red-600 list-disc list-inside space-y-0.5 max-h-32 overflow-y-auto">
                                {resultado.errores.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
                <button onClick={onCerrar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                    Cerrar
                </button>
                <Button onClick={handleSubir} loading={subiendo} disabled={subiendo || !archivo}>
                    <Upload className="h-4 w-4 inline mr-1" /> Subir
                </Button>
            </div>
        </Modal>
    );
};

// ─── Página principal ───────────────────────────────────────────────────────

const PlanMantenimientoPage = () => {
    const [planes, setPlanes] = useState([]);
    const [activos, setActivos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalPlan, setModalPlan] = useState(null); // null = cerrado, {} = nuevo, {...} = editar
    const [modalExcelAbierto, setModalExcelAbierto] = useState(false);
    const [generando, setGenerando] = useState(false);
    const [filtroSede, setFiltroSede] = useState('');

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const [listaPlanes, listaActivos] = await Promise.all([
                planMantenimientoService.listar(),
                activosService.getAll({ limit: 1000 }),
            ]);
            setPlanes(listaPlanes);
            setActivos(listaActivos.items || listaActivos || []);
        } catch (err) {
            toast.error('Error al cargar el plan de mantenimiento');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const handleEliminar = async (id) => {
        if (!window.confirm('¿Eliminar este plan de mantenimiento?')) return;
        try {
            await planMantenimientoService.eliminar(id);
            toast.success('Plan eliminado');
            cargar();
        } catch (err) {
            toast.error('Error al eliminar el plan');
        }
    };

    const handleToggleHabilitado = async (plan) => {
        try {
            await planMantenimientoService.actualizar(plan.id, { habilitado: !plan.habilitado });
            cargar();
        } catch (err) {
            toast.error('Error al actualizar el plan');
        }
    };

    const handleGenerarAhora = async () => {
        setGenerando(true);
        try {
            const res = await planMantenimientoService.generarAhora();
            if (res.ordenes_generadas > 0) {
                toast.success(`${res.ordenes_generadas} orden(es) de trabajo generada(s): ${res.numeros_ot.join(', ')}`);
            } else {
                toast('No hay planes vencidos por generar', { icon: 'ℹ️' });
            }
            cargar();
        } catch (err) {
            toast.error('Error al generar las órdenes');
        } finally {
            setGenerando(false);
        }
    };

    const hoy = new Date().toISOString().split('T')[0];
    const sedes = [...new Set(planes.map((p) => p.sede).filter(Boolean))].sort();
    const planesVisibles = filtroSede === ''
        ? planes
        : filtroSede === '__activos__'
            ? planes.filter((p) => !esPlanPorSede(p))
            : planes.filter((p) => p.sede === filtroSede);

    return (
        <Layout>
            <div className="p-6 max-w-6xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                    <div className="flex items-center space-x-3">
                        <div className="bg-indigo-100 p-2.5 rounded-xl">
                            <CalendarClock className="h-6 w-6 text-indigo-700" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Plan de Mantenimiento</h1>
                            <p className="text-sm text-gray-500">Genera Órdenes de Trabajo automáticamente en los meses o con la frecuencia configurada</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" onClick={handleGenerarAhora} loading={generando} disabled={generando}>
                            <Play className="h-4 w-4 inline mr-1" /> Generar ahora
                        </Button>
                        <button
                            onClick={() => setModalExcelAbierto(true)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                            <Upload className="h-4 w-4 mr-2" /> Cargar Excel
                        </button>
                        <Button onClick={() => setModalPlan({})}>
                            <Plus className="h-4 w-4 inline mr-1" /> Nuevo plan
                        </Button>
                    </div>
                </div>

                {sedes.length > 0 && (
                    <div className="mb-4">
                        <select
                            value={filtroSede}
                            onChange={(e) => setFiltroSede(e.target.value)}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">Todas las sedes y activos ({planes.length})</option>
                            {sedes.map((s) => (
                                <option key={s} value={s}>{s} ({planes.filter((p) => p.sede === s).length})</option>
                            ))}
                            <option value="__activos__">Solo planes por activo</option>
                        </select>
                    </div>
                )}

                {loading ? (
                    <Loading message="Cargando planes..." />
                ) : (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Activo / Sede</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Tipo</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Frecuencia / Meses</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Próxima fecha</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Responsable</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Estado</th>
                                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {planesVisibles.length === 0 && (
                                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Sin planes registrados</td></tr>
                                )}
                                {planesVisibles.map((p) => (
                                    <tr key={p.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            {esPlanPorSede(p) ? (
                                                <>
                                                    <div className="font-semibold text-gray-900">{p.sede}</div>
                                                    {p.area && <div className="text-xs text-gray-400">{p.area}</div>}
                                                </>
                                            ) : (
                                                <>
                                                    <div className="font-semibold text-gray-900">{p.activo?.name || `Activo #${p.activo_id}`}</div>
                                                    <div className="text-xs text-gray-400">{p.activo?.code}</div>
                                                </>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700">
                                            {p.tipo_mantenimiento}
                                            {p.cantidad ? <div className="text-xs text-gray-400">Cantidad: {p.cantidad}</div> : null}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {p.meses
                                                ? parsearMeses(p.meses).map((m) => MESES[m - 1]).join(' · ')
                                                : `cada ${p.frecuencia_valor} ${FRECUENCIA_LABEL[p.frecuencia_tipo]}`}
                                        </td>
                                        <td className={`px-4 py-3 ${p.fecha_proxima <= hoy && p.habilitado ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                                            {p.fecha_proxima}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">{p.responsable || '—'}</td>
                                        <td className="px-4 py-3">
                                            <button onClick={() => handleToggleHabilitado(p)} className="inline-flex items-center text-xs font-semibold">
                                                {p.habilitado ? (
                                                    <span className="inline-flex items-center text-green-700"><PlayCircle className="h-4 w-4 mr-1" /> Activo</span>
                                                ) : (
                                                    <span className="inline-flex items-center text-gray-400"><PauseCircle className="h-4 w-4 mr-1" /> Pausado</span>
                                                )}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => setModalPlan(p)} className="text-gray-400 hover:text-blue-600 mr-2" title="Editar">
                                                <Edit className="h-4 w-4 inline" />
                                            </button>
                                            <button onClick={() => handleEliminar(p.id)} className="text-gray-400 hover:text-red-600" title="Eliminar">
                                                <X className="h-4 w-4 inline" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {modalPlan !== null && (
                <ModalPlan
                    plan={modalPlan.id ? modalPlan : null}
                    activos={activos}
                    sedes={sedes}
                    onCerrar={() => setModalPlan(null)}
                    onGuardado={cargar}
                />
            )}
            {modalExcelAbierto && (
                <ModalCargarExcel onCerrar={() => setModalExcelAbierto(false)} onCargado={cargar} />
            )}
        </Layout>
    );
};

export default PlanMantenimientoPage;
